// Make sure we import our global css after antd so make sure styles are properly overwritten
import 'antd/dist/antd.less';
import '../styles/globals.css';
import 'katex/dist/katex.min.css';

import {
  DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { TolgeeProvider } from '@tolgee/react';
import FlagProvider, { UnleashClient } from '@unleash/proxy-client-react';
import { message } from 'antd';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Session } from 'next-auth';
import { SessionProvider } from 'next-auth/react';
import React, { useEffect, useMemo, useState } from 'react';
import { ParallaxProvider } from 'react-scroll-parallax';
import smoothscroll from 'smoothscroll-polyfill';

import { withErrorBoundary } from '../components/Client500';
import DebugQRCode from '../components/DebugQRCode';
import { AppPropsWithLayout } from '../components/layouts';
import { TelemetryInit } from '../components/TelemetryInit/TelemetryInit';
import UnderMaintenance from '../components/UnderMaintenance';
import { getAvailableLocaleFromCode, getPreferredLocale } from '../hooks/useLocale';
import BackendServiceProvider from '../providers/BackendServiceProvider';
import { CustomIntercomStateProvider } from '../providers/CustomIntercomStateProvider';
import { DailyLimitProvider } from '../providers/DailyLimitProvider';
import { GenerationErrorProvider } from '../providers/GenerationErrorProvider/GenerationErrorProvider';
import { LocaleConfirmer } from '../providers/LocaleConfirmer';
import { MessageProvider } from '../providers/MessageProvider/MessageProvider';
import NextNProgressProvider from '../providers/NextNProgressProvider';
import OfflineNotificationProvider from '../providers/OfflineNotificationProvider';
import { PaywallProvider } from '../providers/PaywallProvider/PaywallProvider';
import RouterAnalyticsProvider from '../providers/RouterAnalyticsProvider';
import RouterHistoryProvider from '../providers/RouterHistoryProvider';
import { tolgee } from '../providers/tolgee';
import TrackingParamsProvider from '../providers/TrackingParamsProvider';
import enUS from '../public/i18n/en-US.json';
import BackendService from '../services/backendService';
import Metrics from '../services/metrics';
import { getBaseUrl } from '../utils/client';
import { handleRouteDataFetchError } from '../utils/routeDataFetchError';
import { UnleashContext } from '../utils/ServerSideFeatureFlagUtils';
import { configureStore } from '../utils/store';
import { applyClientLocaleOverride, buildTolgeeSSRProp } from '../utils/tolgeeUtils';
import { unleashFetchOverride, unleashStorageProviderOverride } from '../utils/unleash';

type AppPageProps = {
  session?: Session;
  unleash?: UnleashContext;
  dehydratedState?: DehydratedState;
  _tolgeeSSR?: { language?: string; staticData?: Record<string, unknown> };
  [key: string]: unknown;
};

type TolgeeSSRProp = React.ComponentProps<typeof TolgeeProvider>['ssr'];

function App({
  Component,
  pageProps: { session, unleash, ...pageProps },
}: AppPropsWithLayout<AppPageProps>) {
  const { config } = configureStore();
  const router = useRouter();
  useState(() => {
    new Metrics();
    Metrics.init(config.mixpanel.token);
  });

  const [queryClient] = useState(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          gcTime: 1000 * 60 * 60 * 24, // 24 hours (formerly cacheTime in v4)
        },
      },
    });
  });

  const [unleashClient] = useState(() => {
    const url = `${getBaseUrl()}${config.unleash.url}`;
    return new UnleashClient({
      ...config.unleash,
      url,
      bootstrap: unleash?.toggles, // Pass toggles fetched SSR
      context: unleash?.context, // Pass context fetched SSR to avoid flicker
      fetch: unleashFetchOverride,
      storageProvider: unleashStorageProviderOverride(),
      environment: 'default',
    });
  });

  const [] = useState(() => {
    dayjs.extend(updateLocale);
    dayjs.extend(localizedFormat);
    dayjs.extend(relativeTime);
  });

  useEffect(() => {
    // Set translate="no" on ant-modal-root when it exists
    const setModalRootTranslate = () => {
      const modalRoot = document.getElementsByClassName('ant-modal-root')?.[0];
      if (modalRoot) {
        modalRoot.setAttribute('translate', 'no');
      }
    };

    // Set immediately if modal root already exists
    setModalRootTranslate();

    // Also set up a MutationObserver to catch dynamically created modal roots
    const observer = new MutationObserver(() => {
      setModalRootTranslate();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const [] = useState(() => {
    const bService = new BackendService();
    bService.init(config.backend.externalApiUrl, config.cdn.url);

    return bService;
  });

  // Gate for applyClientLocaleOverride — see there for why the stored locale has
  // to beat the one in a page's SSR props, and why only after hydration.
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => setIsHydrated(true), []);
  // On /lang/[locale] pages the URL is deliberately the source of truth.
  const isLocalizedPage = router.pathname.startsWith('/lang/[locale]');

  const tolgeeSSRProp = useMemo<TolgeeSSRProp>(() => {
    const ssr = buildTolgeeSSRProp(pageProps._tolgeeSSR, enUS);
    const stored = getPreferredLocale();
    return applyClientLocaleOverride(ssr, {
      clientLocale: stored ? getAvailableLocaleFromCode(stored) : undefined,
      enabled: isHydrated && !isLocalizedPage,
    }) as TolgeeSSRProp;
  }, [pageProps._tolgeeSSR, isHydrated, isLocalizedPage]);

  const getLayout = Component.getLayout ?? (page => page);
  useEffect(() => {
    // kick off the polyfill for smooth scrolling on safari and other browsers
    smoothscroll.polyfill();
  }, []);

  useEffect(() => {
    // A network TypeError during a client transition is not an asset-load
    // error, so Next renders /_error instead of hard-navigating. When the
    // router does emit routeChangeError for that TypeError, load the
    // destination as a document. Cancellations are ignored.
    const onRouteChangeError = (err: Error, url: string) => {
      handleRouteDataFetchError(err, url);
    };
    router.events.on('routeChangeError', onRouteChangeError);
    return () => {
      router.events.off('routeChangeError', onRouteChangeError);
    };
  }, [router.events]);
  const [messageApi, contextHolder] = message.useMessage();

  return (
    <>
      <Head>
        <title>Curipod - Spark curiosity with interactive lessons</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1 viewport-fit=cover"
        />
        <meta
          name="description"
          content={`Free ready to play interactive lessons that sparks your students curiosity in the classroom`}
          key="namedescription"
        />
        <link rel="shortcut icon" href="/static/logo.ico" />
        <meta
          property="og:title"
          content={`Curipod - Spark curiosity with interactive lessons`}
          key="title"
        />
        <meta
          name="twitter:title"
          content={`Curipod - Spark curiosity with interactive lessons`}
        />
        <meta
          property="description"
          content={
            'Free ready to play interactive lessons that sparks your students curiosity in the classroom'
          }
          key="description"
        />
        <meta
          property="og:description"
          content={`Free ready to play interactive lessons that sparks your students curiosity in the classroom`}
          key="og:description"
        />
        <meta
          name="twitter:description"
          content={`Free ready to play interactive lessons that sparks your students curiosity in the classroom`}
        />
        <meta property="og:image" content={'/og_preview.png'} key="imageurl" />
        <meta
          property="og:image:secure_url"
          content={'/og_preview.png'}
          key="imagesecureurl"
        />
        <meta property="og:type" content="website" />

        <meta property="og:url" content={`${getBaseUrl()}`} key="ogurl" />
        <meta property="og:site_name" content="Curipod" />
        <meta property="og:image:type" content={'image/png'} key="imagetype" />
        <meta property="og:image:alt" content={'Curipod logo'} key="imageAlt" />
        <meta property="og:image:width" content={'300'} key="imageWidth" />
        <meta property="og:image:height" content={'190'} key="imageHeight" />
        <meta name="twitter:image" content={'/og_preview.png'} />
        <meta name="twitter:card" content={`summary`} />

        <link rel="canonical" href={`${getBaseUrl()}${router.asPath}`} />
      </Head>

      <TelemetryInit />

      <TrackingParamsProvider>
        <NextNProgressProvider pathname={router.pathname}>
          <CustomIntercomStateProvider>
            <SessionProvider session={session}>
              <RouterAnalyticsProvider>
                <QueryClientProvider client={queryClient}>
                  <HydrationBoundary state={pageProps.dehydratedState}>
                    <RouterHistoryProvider>
                      <ParallaxProvider>
                        <FlagProvider unleashClient={unleashClient}>
                          <UnderMaintenance>
                            <BackendServiceProvider>
                              <TolgeeProvider tolgee={tolgee} ssr={tolgeeSSRProp}>
                                <TranslationBoundary>
                                  {contextHolder}
                                  <MessageProvider messageApi={messageApi}>
                                    <OfflineNotificationProvider>
                                      <PaywallProvider>
                                        <DailyLimitProvider>
                                          <GenerationErrorProvider>
                                            {getLayout(
                                              <>
                                                <Component {...pageProps} />
                                              </>,
                                            )}
                                            <LocaleConfirmer />
                                            <DebugQRCode />
                                          </GenerationErrorProvider>
                                        </DailyLimitProvider>
                                      </PaywallProvider>
                                    </OfflineNotificationProvider>
                                  </MessageProvider>
                                </TranslationBoundary>
                              </TolgeeProvider>
                            </BackendServiceProvider>
                          </UnderMaintenance>
                        </FlagProvider>
                      </ParallaxProvider>
                    </RouterHistoryProvider>
                  </HydrationBoundary>
                  <ReactQueryDevtools buttonPosition="bottom-right" />
                </QueryClientProvider>
              </RouterAnalyticsProvider>
            </SessionProvider>
          </CustomIntercomStateProvider>
        </NextNProgressProvider>
      </TrackingParamsProvider>
    </>
  );
}

function TranslationBoundary({ children }: { children: React.ReactNode }) {
  return (
    <div
      id="curipod-translation-boundary"
      translate="no"
      style={{ height: '100%', width: '100%' }}
    >
      {children}
    </div>
  );
}

export default withErrorBoundary(App);
